import React from "react";

/**
 * Circular agent avatar with initials fallback.
 *
 *   <AgentAvatar src={agent.photo_url} name={agent.name} size={40} />
 *
 * `src` is relative (/api/files/...). We prefix it with the backend origin so
 * the cookie-authenticated image endpoint serves it directly.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function initialsOf(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "?";
}

// Deterministic background tint from the name so avatars look consistent across pages
function tintOf(name) {
  if (!name) return "#0E7A3D";
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  const palette = ["#0E7A3D", "#0A6633", "#C97A1C", "#7A1C8F", "#1F6FB8", "#B81F3C"];
  return palette[hash % palette.length];
}

export default function AgentAvatar({ src, name, size = 40, className = "", testid = "agent-avatar" }) {
  const px = `${size}px`;
  const ring = Math.max(2, Math.round(size / 16));
  const fontSize = Math.max(10, Math.round(size / 2.6));
  const fullSrc = src ? (src.startsWith("http") ? src : `${BACKEND}${src}`) : null;
  return (
    <span
      data-testid={testid}
      data-has-photo={fullSrc ? "1" : "0"}
      title={name}
      className={`relative inline-flex items-center justify-center rounded-full overflow-hidden shrink-0 ${className}`}
      style={{
        width: px,
        height: px,
        backgroundColor: tintOf(name),
        boxShadow: `0 0 0 ${ring}px rgba(255,255,255,0.85), 0 0 0 ${ring + 1}px rgba(0,0,0,0.05)`,
      }}
    >
      {fullSrc ? (
        <img
          src={fullSrc}
          alt={name || "Agent"}
          loading="lazy"
          referrerPolicy="no-referrer"
          className="w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <span style={{ fontSize, color: "white", fontWeight: 800, letterSpacing: 0.5 }}>
          {initialsOf(name)}
        </span>
      )}
    </span>
  );
}
